import { Box, Link as MuiLink } from "@mui/material";
import {
  AccountBalance,
  AccountBalanceWallet,
  ChevronRight,
  ExpandMore,
} from "@mui/icons-material";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import intl from "react-intl-universal";
import { Link as RouterLink } from "react-router-dom";
import { ModalLoader } from "../../Atoms/ModalLoader";
import { Pagination } from "@mui/lab";
import moment from "moment";
import "moment/locale/pt-br";
import {
  formatCurrency,
  formatCurrencyType,
} from "../../../Utils/formatters";
import { mapStatementTransactionRow } from "../../../Utils/mapStatementTransactionRow";
import { useAuth } from "../../../Contexts/AuthContext";
import {
  resolveStatementCategoryIcon,
} from "../../../Pages/Private/BaasConsole/Extrato/baasCdnIcons";
import { usePixStatementDetailFlow } from "../../../Pages/Private/Pix/usePixStatementDetailFlow";
import { MdOutlinePix } from "react-icons/md";
import {
  GRID_HISTORY_COLS_EXTRACT,
  GRID_HISTORY_HEADER_VALUE_CLASS,
  GRID_HISTORY_SUBGRID_SECTION,
  GridHistoryCell,
  GridHistoryContainer,
  GridHistoryHeader,
  GridHistoryRow,
} from "../GridHistory";
import type { TransactionType } from "../../../Utils/getTransactionsByDay";

moment.locale("pt-br");

type DayGroup = Record<string, TransactionType[]>;

const formatDayHeader = (dateStr: string) =>
  moment(dateStr).format("DD MMMM YYYY").toUpperCase();

const formatSignedCurrency = (value: number) => {
  const formatted = formatCurrency(Math.abs(value));
  if (value > 0) return `+${formatted}`;
  if (value < 0) return `-${formatted}`;
  return formatted;
};

const TransactionListHeader = () => (
  <GridHistoryHeader colSpan={3}>
    <span>{intl.get("grid.entry")}</span>
    <span className="text-right">{intl.get("grid.time")}</span>
    <span className={GRID_HISTORY_HEADER_VALUE_CLASS}>{intl.get("grid.value")}</span>
  </GridHistoryHeader>
);

const TransactionRow = ({
  el,
  isClickable,
  isCredit,
  onItemClick,
}: {
  el: {
    title?: string;
    subtitle?: string;
    meta?: string;
    time: string;
    value: string;
    icon?: string;
  };
  isClickable: boolean;
  isCredit: boolean;
  onItemClick: () => void;
}) => (
  <GridHistoryRow
    colSpan={3}
    className={
      isClickable
        ? "cursor-pointer transition hover:bg-gray-50 dark:hover:bg-[#1f1f1f]"
        : ""
    }
    onClick={isClickable ? onItemClick : undefined}
    role={isClickable ? "button" : undefined}
    tabIndex={isClickable ? 0 : undefined}
    onKeyDown={(event) => {
      if (isClickable && (event.key === "Enter" || event.key === " ")) {
        onItemClick();
      }
    }}
  >
    <GridHistoryCell label={intl.get("grid.entry")}>
      <div className="flex min-w-0 items-center gap-3">
        <TransactionIcon src={el.icon} />
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold leading-snug text-brand-dark dark:text-gray-100">
            {el.title}
          </p>
          {el.subtitle ? (
            <p className="mt-0.5 truncate text-xs leading-snug text-gray-500 dark:text-gray-400">
              {el.subtitle}
            </p>
          ) : null}
          {el.meta ? (
            <p className="mt-1 truncate text-[11px] leading-snug text-gray-400 dark:text-gray-500">
              {el.meta}
            </p>
          ) : null}
        </div>
      </div>
    </GridHistoryCell>
    <GridHistoryCell label={intl.get("grid.time")} align="right">
      <p className="text-xs tabular-nums text-gray-500 dark:text-gray-400">{el.time}</p>
    </GridHistoryCell>
    <GridHistoryCell label={intl.get("grid.value")} align="right">
      <p
        className={`text-sm font-semibold ${
          isCredit
            ? "text-emerald-600 dark:text-emerald-400"
            : "text-red-600 dark:text-red-400"
        }`}
      >
        {el.value}
      </p>
    </GridHistoryCell>
  </GridHistoryRow>
);

const DateDivider = ({ label, isFirst }: { label: string; isFirst: boolean }) => (
  <div
    className={`px-4 py-2.5 ${
      !isFirst ? "border-t border-gray-100 dark:border-brand-dark-border" : ""
    }`}
  >
    <p className="dashboard-section-label m-0">{label}</p>
  </div>
);

const BalanceIcon = ({ children }: { children: ReactNode }) => (
  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-500 dark:bg-[#262626] dark:text-gray-400">
    {children}
  </span>
);

const BalanceRow = ({
  title,
  amount,
  variant,
}: {
  title: string;
  amount: string;
  variant: "opening" | "closing";
}) => (
  <div className="col-span-3 flex flex-col gap-2 border-b border-gray-100 bg-gray-50/80 px-4 py-3 last:border-b-0 dark:border-brand-dark-border dark:bg-brand-dark-elevated/60 sm:flex-row sm:items-center sm:justify-between">
    <div className="flex min-w-0 flex-1 items-center gap-3">
      <BalanceIcon>
        {variant === "opening" ? (
          <AccountBalanceWallet sx={{ fontSize: 18 }} aria-hidden />
        ) : (
          <AccountBalance sx={{ fontSize: 18 }} aria-hidden />
        )}
      </BalanceIcon>
      <p className="min-w-0 truncate text-sm font-semibold text-brand-dark dark:text-gray-100">
        {title}
      </p>
    </div>
    <p className="shrink-0 text-sm font-semibold tabular-nums text-brand-dark dark:text-gray-100 sm:text-right">
      {amount}
    </p>
  </div>
);

const TransactionIcon = ({ src }: { src?: string }) => {
  const resolved = resolveStatementCategoryIcon(src);
  const [broken, setBroken] = useState(false);
  const isBrandPix =
    Boolean(resolved) && /static\.valut\.app\/balance-icons\/pix\.svg/i.test(resolved);
  const isValutBalanceIcon =
    Boolean(resolved) && /static\.valut\.app\/balance-icons\//i.test(resolved);
  const isCircularCdn =
    !broken &&
    Boolean(resolved) &&
    !isBrandPix &&
    !isValutBalanceIcon &&
    (/cdn\.valut\.services\/assets\/icons\//i.test(resolved) ||
      /\/cdn\/icons\//i.test(resolved));
  return (
    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gray-100 dark:bg-[#262626]">
      {resolved && !broken ? (
        <img
          src={resolved}
          alt=""
          onError={() => setBroken(true)}
          className={
            isCircularCdn
              ? "h-9 w-9 rounded-full object-cover"
              : "h-4 w-auto max-w-[18px]"
          }
        />
      ) : (
        <MdOutlinePix className="h-5 w-5 text-[#e7a73f]" aria-hidden />
      )}
    </span>
  );
};

type ReceiptListProps = {
  itens: {
    items?: DayGroup[];
    pageCount?: number;
  };
  loading?: boolean;
  type?: string;
  title?: string;
  headerLink?: string;
  hasFilter?: boolean | string;
  product?: string;
  handleChangePage?: (event: unknown, value: number) => void;
  page?: number;
  hidePagination?: boolean;
  dayAccordion?: boolean;
  refundEnabled?: boolean;
  onRefundSuccess?: () => void;
};

export const ReceiptList = ({
  itens,
  loading,
  type,
  title,
  headerLink,
  hasFilter: _hasFilter,
  product: _product,
  handleChangePage,
  page,
  hidePagination = false,
  dayAccordion = false,
  refundEnabled = true,
  onRefundSuccess,
}: ReceiptListProps) => {
  const { hasPermission } = useAuth();
  const { openDetails, detailModals } = usePixStatementDetailFlow({
    refundEnabled,
    onRefundSuccess,
  });

  const dayGroups = itens.items ?? [];

  const dayKeys = useMemo(
    () =>
      dayGroups
        .map((receipt) => (receipt ? Object.keys(receipt)[0] : null))
        .filter((day): day is string => Boolean(day))
        .join("|"),
    [dayGroups],
  );

  const [expandedDays, setExpandedDays] = useState<Set<string>>(() => new Set());
  const lastInitializedDayKeysRef = useRef<string | null>(null);

  useEffect(() => {
    if (!dayAccordion || !dayKeys) return;
    if (lastInitializedDayKeysRef.current === dayKeys) return;

    lastInitializedDayKeysRef.current = dayKeys;
    const today = moment().format("YYYY-MM-DD");
    const days = dayKeys.split("|");
    const expanded = new Set<string>();

    days.forEach((day, groupIndex) => {
      if (groupIndex === 0 || day === today) {
        expanded.add(day);
      }
    });

    setExpandedDays(expanded);
  }, [dayAccordion, dayKeys]);

  const toggleDay = (day: string) => {
    setExpandedDays((prev) => {
      const next = new Set(prev);
      if (next.has(day)) {
        next.delete(day);
      } else {
        next.add(day);
      }
      return next;
    });
  };

  if (loading) {
    return <ModalLoader open={loading} text={"Carregando informações"} />;
  }

  const renderDayTransactions = (
    day: string,
    listReceipt: TransactionType[],
  ) => {
    const chronological = [...listReceipt].sort((a, b) => {
      const ta = moment(a?.created_at).valueOf();
      const tb = moment(b?.created_at).valueOf();
      if (Number.isFinite(ta) && Number.isFinite(tb) && ta !== tb) return ta - tb;
      return String(a?.id ?? "").localeCompare(String(b?.id ?? ""));
    });
    const firstItem = chronological[0];
    const lastItem = chronological[chronological.length - 1];
    const initialBalance = formatCurrency(
      Math.abs(firstItem?.balance_before ?? 0),
    );
    const finalBalance = formatCurrency(
      Math.abs(lastItem?.balance_after ?? 0),
    );

    return (
      <>
        <BalanceRow
          title="Saldo inicial do dia"
          amount={initialBalance}
          variant="opening"
        />

        {listReceipt.map((item, index) => {
          const amountType = formatCurrencyType(item?.amount);
          const isCredit = amountType === "credit";
          const opensStatementDetail = type === "receipts" || type === "extract";

          const row = mapStatementTransactionRow(item);
          const el = {
            ...item,
            title: row.title,
            subtitle: row.subtitle,
            meta: row.meta,
            date: moment(item?.created_at).format(
              "DD/MM/YYYY [às] HH:mm",
            ),
            value: formatSignedCurrency(item?.amount),
            balance_after: formatCurrency(Math.abs(item?.balance_after)),
            balance_before: formatCurrency(
              Math.abs(item?.balance_before),
            ),
            icon: item?.category?.icon,
            time: row.data,
          };

          return (
            <TransactionRow
              key={item?.id ?? `${day}-${index}`}
              el={el}
              isClickable={opensStatementDetail}
              isCredit={isCredit}
              onItemClick={() => openDetails(item)}
            />
          );
        })}

        <BalanceRow
          title="Saldo final do dia"
          amount={finalBalance}
          variant="closing"
        />
      </>
    );
  };

  const renderDayGroup = (receipt: DayGroup, groupIndex: number) => {
    if (!receipt) return null;

    const day = Object.keys(receipt)[0];
    const listReceipt = receipt[day] ?? [];
    if (listReceipt.length === 0) return null;

    const lastItem = listReceipt[listReceipt.length - 1];
    const finalBalance = formatCurrency(
      Math.abs(lastItem?.balance_after ?? 0),
    );
    const isExpanded = expandedDays.has(day);

    if (dayAccordion) {
      return (
        <div
          key={day}
          className={`${GRID_HISTORY_SUBGRID_SECTION} extract-day-accordion ${groupIndex > 0 ? "border-t border-gray-100 dark:border-brand-dark-border" : ""}`}
        >
          <button
            type="button"
            className="extract-day-accordion__header col-span-3"
            onClick={() => toggleDay(day)}
            aria-expanded={isExpanded}
          >
            <div className="min-w-0 text-left">
              <p className="dashboard-section-label m-0">{formatDayHeader(day)}</p>
              <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                {listReceipt.length} lançamento{listReceipt.length !== 1 ? "s" : ""}
                {" · "}
                Saldo final {finalBalance}
              </p>
            </div>
            <ExpandMore
              className={`extract-day-accordion__chevron ${isExpanded ? "extract-day-accordion__chevron--open" : ""}`}
              sx={{ fontSize: 22 }}
            />
          </button>

          {isExpanded ? (
            <div className={`extract-day-accordion__panel ${GRID_HISTORY_SUBGRID_SECTION}`}>
              {renderDayTransactions(day, listReceipt)}
            </div>
          ) : null}
        </div>
      );
    }

    return (
      <div key={day} className="contents">
        <div className="col-span-3">
          <DateDivider label={formatDayHeader(day)} isFirst={groupIndex === 0} />
        </div>
        {renderDayTransactions(day, listReceipt)}
      </div>
    );
  };

  return (
    <>
      {!!headerLink && hasPermission("statementsview") && (
        <div className="flex items-center justify-between border-b border-gray-100 px-4 py-2.5 dark:border-brand-dark-border md:px-5">
          <h2 className="m-0 text-base font-semibold text-gray-900 dark:text-gray-100">
            {title}
          </h2>
          <MuiLink
            component={RouterLink}
            underline="none"
            to={headerLink}
            className="inline-flex items-center gap-0.5 text-xs font-semibold text-[#D3A971] no-underline transition hover:opacity-70"
          >
            Ver mais
            <ChevronRight sx={{ fontSize: 16 }} />
          </MuiLink>
        </div>
      )}

      <div className="min-w-0 w-full overflow-x-hidden">
        {dayGroups.length === 0 ? (
          <div className="app-empty-state m-4">Nenhum resultado encontrado.</div>
        ) : (
          <GridHistoryContainer gridCols={GRID_HISTORY_COLS_EXTRACT}>
            <TransactionListHeader />
            {dayGroups.map((receipt, groupIndex) => renderDayGroup(receipt, groupIndex))}
          </GridHistoryContainer>
        )}
      </div>

      {!hidePagination && (type === "extract" || type === "receipts") && dayGroups.length > 0 && (
        <Box className="mb-2 mt-3 px-3 md:px-4">
          <Pagination
            count={itens.pageCount}
            page={page}
            onChange={handleChangePage}
            variant="outlined"
            shape="rounded"
          />
        </Box>
      )}

      {detailModals}
    </>
  );
};

ReceiptList.propTypes = {};
